import { safeExternalUrl } from '../../lib/url';
import type { CommunityTemplateGalleryRead, CommunityTemplateRead } from '../../types/api';
import { parseCommunityZones } from './communityZones';

/**
 * The hand-off from the community gallery to the brief, and the paths of the
 * studio behind it.
 *
 * A gallery tile navigates to `/generation` carrying the template in router
 * state — the same mechanism a pack tile uses, so there is one way a template
 * reaches the brief. Router state is *untrusted input*: it survives history
 * entries, it is writable from the console and a refresh replays whatever was
 * last pushed, so nothing here trusts its shape. A tile that does not carry an
 * id, a title and a readable zone map is not a template.
 */

/** The member's own templates — the studio's entry point. */
export const COMMUNITY_TEMPLATES_PATH = '/templates/community';

/** The studio, on a template that does not exist yet. */
export const COMMUNITY_TEMPLATE_NEW_PATH = '/templates/community/new';

/** The studio, on one the member already filed. */
export function communityTemplatePath(templateId: string): string {
  return `${COMMUNITY_TEMPLATES_PATH}/${templateId}`;
}

/** Key the template travels under in router state. One place, so it cannot drift. */
export const COMMUNITY_TEMPLATE_STATE_KEY = 'communityTemplate';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * The template a brief started from, out of `useLocation().state`, or null when
 * the state is anything else — a plain navigation, a stale entry, a
 * hand-written object.
 *
 * What survives is enough to pin the card and to send `community_template_id`:
 * the id, the title, the description, the preview, the backdrop image and the
 * zone map, which decides whether the brief shows an item row and, on a v2
 * map, which text fields.
 */
export function readCommunityTemplatePrefill(state: unknown): CommunityTemplateGalleryRead | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = (state as Record<string, unknown>)[COMMUNITY_TEMPLATE_STATE_KEY];
  if (typeof candidate !== 'object' || candidate === null) return null;

  const record = candidate as Record<string, unknown>;
  if (!isNonEmptyString(record.id) || !isNonEmptyString(record.title)) return null;

  const zones = parseCommunityZones(record.zones);
  if (!zones) return null;

  return {
    id: record.id,
    title: record.title,
    description: typeof record.description === 'string' ? record.description : '',
    zones,
    use_count: typeof record.use_count === 'number' ? record.use_count : 0,
    // The preview becomes an `img src`, so it has to clear the same http(s)
    // check every other link off this app does.
    preview_url: isNonEmptyString(record.preview_url) ? safeExternalUrl(record.preview_url) : null,
    // The backdrop of an image design; null on a colour or gradient one.
    image_url: isNonEmptyString(record.image_url) ? safeExternalUrl(record.image_url) : null,
  };
}

/**
 * The picture a tile shows for one template: the design itself, drawn by the
 * shared CSS preview so a colour or gradient design needs no image at all.
 * The backdrop is the design's own image when it has one — `image_url`, or
 * `preview_url` on a v1 template, where it is the author's upload.
 */
export function communityBackdropUrl(template: CommunityTemplateGalleryRead): string | null {
  return safeExternalUrl(
    template.image_url ?? (template.zones.v === 1 ? template.preview_url : null),
  );
}

/**
 * One of the member's own templates, in the shape the brief pins.
 *
 * A business may post on its own template in any status — a draft, one with
 * the reviewers, one sent back — so the pin cannot wait for the gallery to
 * carry it. The only difference from a gallery tile is the picture: an own
 * image design lives in the private `assets` bucket, so the caller signs
 * `image_path` and passes that URL in as the backdrop. It is an ordinary https
 * URL and clears `readCommunityTemplatePrefill` like a public one.
 *
 * `preview_url` stays null: it is the gallery's public copy, which an own
 * template only has once approved, and the backdrop already shows the design.
 */
export function ownCommunityTemplatePin(
  template: CommunityTemplateRead,
  signedImageUrl: string | null,
): CommunityTemplateGalleryRead {
  return {
    id: template.id,
    title: template.title,
    description: template.description,
    zones: template.zones,
    use_count: template.use_count,
    preview_url: null,
    image_url: template.image_path ? safeExternalUrl(signedImageUrl) : null,
  };
}
